import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, X, Upload, Image as ImageIcon, ImagePlus, Trash2, LoaderCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { adminApi } from "@/lib/api";

export interface ImageUploaderProps {
  images: string[];
  onChange: (images: string[]) => void;
}

export function ImageUploader({ images = [], onChange }: ImageUploaderProps) {
  const { toast } = useToast();
  const [newImageUrl, setNewImageUrl] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;

    const file = e.target.files[0];
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "Archivo demasiado grande",
        description: "La imagen debe ser menor a 5MB",
        variant: "destructive",
      });
      return;
    }

    setUploading(true);
    try {
      const imageUrl = await adminApi.products.uploadImage(file);
      onChange([...images, imageUrl]);
      toast({
        title: "Imagen subida",
        description: "La imagen se ha subido correctamente",
      });
    } catch (error: any) {
      toast({
        title: "Error al subir la imagen",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleRemoveImage = (index: number) => {
    const newImages = [...images];
    newImages.splice(index, 1);
    onChange(newImages);
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    e.dataTransfer.setData("index", index.toString());
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    const dragIndex = parseInt(e.dataTransfer.getData("index"));
    if (dragIndex === dropIndex) return;

    const newImages = [...images];
    const temp = newImages[dragIndex];
    newImages.splice(dragIndex, 1);
    newImages.splice(dropIndex, 0, temp);
    onChange(newImages);
  };

  // Función para normalizar URLs de imágenes
  const normalizeImageUrl = (url: string): string => {
    // Si ya es una URL completa, devolverla tal cual
    if (url.startsWith('http') && !url.includes('supabase.co')) {
      return url;
    }

    // Si es una URL de Supabase, extraer la ruta del archivo
    if (url.includes('supabase.co') && url.includes('/storage/')) {
      const pathMatch = url.match(/\/images\/(.+)$/);
      if (pathMatch && pathMatch[1]) {
        // Usar la ruta directa
        return `https://jlufgkgssnxumguvknhc.supabase.co/storage/v1/object/public/images/${pathMatch[1]}`;
      }
    }

    // Si es una ruta relativa, construir la URL completa
    const cleanPath = url.startsWith('/') ? url.substring(1) : url;

    // Si la ruta ya incluye 'products/', usarla tal cual
    if (cleanPath.includes('products/')) {
      return `https://jlufgkgssnxumguvknhc.supabase.co/storage/v1/object/public/images/${cleanPath}`;
    }

    // Si no, asumir que es una ruta dentro de products/
    return `https://jlufgkgssnxumguvknhc.supabase.co/storage/v1/object/public/images/products/${cleanPath}`;
  };

  // Función para añadir una imagen por URL
  const handleAddImageByUrl = () => {
    if (!newImageUrl.trim()) return;

    // Validación simple de URL
    try {
      new URL(newImageUrl); // Esto lanzará un error si la URL no es válida

      // Normalizar la URL
      const normalizedUrl = normalizeImageUrl(newImageUrl.trim());

      // Añadir la URL a la lista de imágenes
      onChange([...images, normalizedUrl]);
      setNewImageUrl("");

      toast({
        title: "URL de imagen añadida",
        description: "La URL de la imagen se ha añadido al producto.",
      });
    } catch (error) {
      toast({
        title: "URL no válida",
        description: "Por favor, introduce una URL válida.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-4">
      <Label>Imágenes del producto</Label>

      <div className="grid grid-cols-3 gap-2">
        {images.map((image, index) => (
          <div
            key={index}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, index)}
            className="relative group border border-zinc-700 rounded-md overflow-hidden aspect-square h-24"
          >
            {uploadingIndex === index ? (
              <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                <LoaderCircle className="w-6 h-6 text-white animate-spin" />
              </div>
            ) : (
              <>
                <img
                  src={image}
                  alt={`Imagen ${index + 1}`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    // Si la imagen no se puede cargar, mostrar un placeholder
                    const target = e.target as HTMLImageElement;
                    target.src = "/placeholder-image.png";
                  }}
                />
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveImage(index)}
                    className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                  >
                    <Trash2 className="h-5 w-5" />
                  </Button>
                </div>
              </>
            )}
          </div>
        ))}
        {images.length < 10 && (
          <label className="border border-dashed border-zinc-700 rounded-md overflow-hidden aspect-square h-24 flex flex-col items-center justify-center bg-zinc-800/50 cursor-pointer hover:bg-zinc-800 transition-colors">
            {uploading ? (
              <LoaderCircle className="w-6 h-6 text-white animate-spin" />
            ) : (
              <>
                <ImagePlus className="w-6 h-6 text-zinc-500 mb-1" />
                <span className="text-xs text-zinc-500">Añadir imagen</span>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageUpload}
                  disabled={uploading}
                />
              </>
            )}
          </label>
        )}
      </div>

      {/* Sección de URL de imagen */}
      <div>
        <Label>O añade una URL de imagen</Label>
        <div className="flex gap-2 mt-1">
          <Input
            value={newImageUrl}
            onChange={(e) => setNewImageUrl(e.target.value)}
            placeholder="https://ejemplo.com/imagen.jpg"
            className="flex-1"
          />
          <Button
            type="button"
            variant="outline"
            onClick={handleAddImageByUrl}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <p className="text-xs text-zinc-500">
        Sube hasta 10 imágenes (máx. 5MB cada una). Arrastra para ordenar.
      </p>
    </div>
  );
}
